import ToPayNow from "common/components/subscriptions/ToPayNow";
import Skeleton from "common/components/ui/Skeleton";
import useCustomerEstimateCreation from "common/datahooks/useCustomerEstimateCreation";
import useCustomerEstimateRenewal from "common/datahooks/useCustomerEstimateRenewal";
import useCustomerEstimateUpdate from "common/datahooks/useCustomerEstimateUpdate";
import usePromotions from "common/datahooks/usePromotions";
import { HTTPError } from "common/helpers/HTTP";
import { formatPrice, formatPricingPeriodInfo } from "common/helpers/utils";
import type { Pricing } from "common/types";

export default function PricingDetails({
  pricing,
  seatCount,
  includedSeats,
  isDowngrade,
  alertsComponent,
}: {
  pricing: { addon: Pricing; plan: Pricing };
  seatCount: number;
  includedSeats: number;
  isDowngrade: boolean;
  alertsComponent: JSX.Element;
}) {
  const { agencyPlan } = usePromotions();
  const { estimateRenewal, estimateRenewalError, isLoadingEstimateRenewal } =
    useCustomerEstimateRenewal(agencyPlan?.subscription);
  const { estimateUpdate, estimateUpdateError, isLoadingEstimateUpdate } =
    useCustomerEstimateUpdate({
      pricing,
      quantity: seatCount,
      prorate: false,
      subscription: agencyPlan?.subscription,
    });
  const {
    estimateUpdate: proRatedEstimateUpdate,
    isLoadingEstimateUpdate: isLoadingProratedEstimateUpdate,
    estimateUpdateError: proRatedEstimateUpdateError,
  } = useCustomerEstimateUpdate({
    pricing,
    quantity: seatCount,
    prorate: true,
    subscription: agencyPlan?.subscription,
  });
  const { estimateCreation, isLoadingEstimateCreation, estimateCreationError } =
    useCustomerEstimateCreation({
      planPricingId: pricing.plan.id,
      quantity: seatCount,
      subscription: agencyPlan?.subscription,
    });
  const error =
    estimateCreationError ||
    estimateUpdateError ||
    estimateRenewalError ||
    proRatedEstimateUpdateError;

  const currentEstimate = estimateCreation ?? estimateRenewal;
  const isLoadingCurrentEstimate =
    isLoadingEstimateCreation || isLoadingEstimateRenewal;
  const additionalSeatsCount = seatCount - includedSeats;

  const currentPricing = agencyPlan?.subscription
    ? agencyPlan.subscription.item_price
    : pricing.plan;
  const isChange =
    agencyPlan?.subscription.status === "active" &&
    (agencyPlan.license_count !== seatCount ||
      agencyPlan.subscription.item_price.id !== pricing.plan.id);

  return (
    <>
      <div className="flex items-center justify-between px-2 py-1 bg-black-100 rounded-xl">
        <div className="flex flex-col text-black-600 text-button-sm">
          <span>Base price</span>
          <span>({includedSeats} seats included)</span>
        </div>
        {isLoadingCurrentEstimate || !currentEstimate ? (
          <Skeleton className="h-5 w-28" />
        ) : (
          <span className="text-md-medium text-black-500">
            {formatPrice(
              (currentEstimate.pricePerItem / 100) * includedSeats +
                currentEstimate.basePrice / 100,
            )}
            /{formatPricingPeriodInfo(currentPricing)}
          </span>
        )}
      </div>
      {!!additionalSeatsCount && (
        <div className="flex items-center justify-between px-2">
          <div className="flex flex-col gap-y-0.5">
            <span className="text-black-600 text-button-sm">
              Additional seats
            </span>
            <span className="text-purple-600 text-xs-bold">
              Billed 20$ per seat
            </span>
          </div>
          {isLoadingCurrentEstimate || !currentEstimate ? (
            <Skeleton className="h-5 w-28" />
          ) : (
            <span className="text-md-medium text-black-500">
              {formatPrice(
                additionalSeatsCount * (currentEstimate.pricePerItem / 100),
              )}
              /{formatPricingPeriodInfo(currentPricing)}
            </span>
          )}
        </div>
      )}
      <div className="flex items-center justify-between px-2">
        <span className="text-button-sm text-black-600">No. of seats</span>
        <span className="text-md-medium text-black-500">
          {agencyPlan ? agencyPlan.license_count : seatCount}
        </span>
      </div>
      <div className="flex items-center justify-between px-2">
        <span className="text-button-sm text-black-600">Price</span>
        {isLoadingCurrentEstimate || !currentEstimate ? (
          <Skeleton className="h-5 w-28" />
        ) : (
          <span className="text-md-medium text-black-500">
            {formatPrice(currentEstimate.amountDue / 100)}/
            {formatPricingPeriodInfo(currentPricing)}
          </span>
        )}
      </div>
      {isChange && (
        <>
          <div className="w-full h-px bg-black-200" />
          <div className="flex items-center justify-between px-2">
            <span className="text-button-sm text-black-600">
              New no. of seats
            </span>
            <span className="text-black-950 text-md-bold">{seatCount}</span>
          </div>
          <div className="flex items-center justify-between px-2">
            <span className="text-button-sm text-black-600">New price</span>
            {isLoadingEstimateUpdate || !estimateUpdate ? (
              <Skeleton className="h-5 w-28" />
            ) : (
              <span className="text-black-950 text-md-bold">
                {formatPrice(estimateUpdate.amountDue / 100)}/
                {formatPricingPeriodInfo(pricing.plan)}
              </span>
            )}
          </div>
        </>
      )}
      {alertsComponent}
      <ToPayNow
        estimate={estimateCreation ? estimateCreation : proRatedEstimateUpdate}
        isLoading={isLoadingEstimateCreation || isLoadingProratedEstimateUpdate}
        isDowngradeOrEqual={isDowngrade || (!isChange && !!agencyPlan)}
        isInTrial={false}
      />
      {error && (
        <span className="text-red-500 text-xs">
          {error instanceof HTTPError && error.serverMessage
            ? error.serverMessage
            : "Error loading estimate, please try again later."}
        </span>
      )}
    </>
  );
}
